import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { KHIDMAT_DETAILS, VENUE_LIST, WHATSPP_LINK } from '../core/constant';
import { getCoordinatorsByService } from '../core/api';

const KhidmatDetail = () => {
    const { khidmatKey } = useParams();
    const [coordinatorsData, setCoordinatorsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Find the khidmat details
    const khidmatDetail = KHIDMAT_DETAILS.find(
        service => service.key.toLowerCase() === khidmatKey.toLowerCase()
    );

    useEffect(() => {
        const fetchCoordinators = async () => {
            if (!khidmatDetail) return;

            try {
                setLoading(true);
                const response = await getCoordinatorsByService(khidmatDetail.key);
                console.log('Fetched coordinators:', response);
                // API returns data organized by venue: {BURHANI: {coordinatorList: [], meetingPoint: {}}, ...}
                setCoordinatorsData(response || {});
            } catch (err) {
                setError('Failed to load coordinators');
                console.error('Error fetching coordinators:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCoordinators();
    }, [khidmatDetail]);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // If khidmat not found
    if (!khidmatDetail) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">Khidmat Not Found</h1>
                    <p className="text-gray-600 mb-8">The requested khidmat service could not be found.</p>
                    <Link
                        to="/"
                        className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                    >
                        Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    // Get meeting point for venue from API data or fallback to constants
    const getMeetingPoint = (venueId) => {
        const venueData = coordinatorsData[venueId];
        if (venueData?.meetingPoint) {
            // If API provides meetingPoint data
            if (typeof venueData.meetingPoint === 'string') {
                return venueData.meetingPoint;
            } else if (venueData.meetingPoint.location && venueData.meetingPoint.time) {
                return `${venueData.meetingPoint.location} • ${venueData.meetingPoint.time}`;
            }
        }
        return 'Contact coordinators for meeting point';
    };

    // Check if any coordinators are available
    const hasAnyCoordinators = Object.values(coordinatorsData).some(venueData =>
        venueData?.coordinatorList && venueData.coordinatorList.length > 0
    );

    return (
        <div className="min-h-screen bg-gray-50 overflow-x-hidden">
            {/* Fixed Back Button - Bottom left */}
            <Link
                to="/"
                className="fixed bottom-6 left-6 z-50 inline-flex items-center text-white hover:text-amber-200 transition-all duration-300 bg-amber-600 hover:bg-amber-700 shadow-lg hover:shadow-xl px-4 py-3 rounded-full border border-amber-500 transform hover:scale-105"
            >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back to Home
            </Link>

            {/* Hero Section with Background Image */}
            <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src={khidmatDetail.image}
                        alt={khidmatDetail.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/50"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center text-white overflow-hidden">
                    <div className="max-w-4xl mx-auto">
                        <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-6 font-serif leading-tight break-words">
                            {khidmatDetail.title}
                        </h1>
                        {khidmatDetail.subTitle && (
                            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-amber-200 mb-8 font-medium break-words">
                                {khidmatDetail.subTitle}
                            </p>
                        )}
                        <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/90 break-words">
                            {khidmatDetail.description}
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                {/* Coordinators Section */}
                <section className="mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center font-serif">
                        Coordinators & Meeting Points
                    </h2>

                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                            <p className="mt-4 text-gray-600">Loading coordinators...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 bg-red-50 rounded-xl border border-red-200">
                            <p className="text-red-600">{error}</p>
                        </div>
                    ) : !hasAnyCoordinators ? (
                        <div className="text-center py-12 bg-yellow-50 rounded-xl border border-yellow-200">
                            <p className="text-yellow-700">No coordinators assigned yet for this khidmat service.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 lg:gap-8">
                            {Object.keys(coordinatorsData).sort().map((venueId) => {
                                if( VENUE_LIST.find(v => v.venueId === venueId) === undefined) {
                                    return null; // Skip unknown venues
                                }
                                const venueData = coordinatorsData[venueId];
                                const venueCoordinators = venueData?.coordinatorList || [];
                                const meetingPointInfo = getMeetingPoint(venueId);
                                const venue = VENUE_LIST.find(v => v.venueId === venueId) || { venueId, name: venueId };

                                return (
                                    <div key={venueId} className="flex-1 bg-white rounded-xl shadow-lg border border-gray-200 min-w-0 overflow-hidden">
                                        <div className="p-4 sm:p-6">
                                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2 break-words">
                                                {venue.name}
                                            </h3>

                                            {/* Meeting Point */}
                                            <div className="mb-4 sm:mb-6">
                                                <h4 className="font-semibold text-amber-700 mb-2 text-sm sm:text-base">Meeting Point & Time:</h4>
                                                <p className="text-gray-600 text-xs sm:text-sm bg-amber-50 p-2 sm:p-3 rounded-lg border border-amber-200 break-words overflow-wrap-anywhere">
                                                    📍 {meetingPointInfo}
                                                </p>
                                            </div>

                                            {/* Coordinators */}
                                            <div>
                                                <h4 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Coordinators:</h4>
                                                {venueCoordinators.length === 0 ? (
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-center">
                                                        <p className="text-gray-500 text-xs sm:text-sm break-words">
                                                            Coordinators will be assigned soon.
                                                            <br />
                                                            Check back later or contact via WhatsApp.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-3">
                                                        {venueCoordinators.map((coordinator, index) => (
                                                            <div key={index} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 min-w-0 overflow-hidden">
                                                                <p className="font-semibold text-gray-800 text-sm sm:text-base break-words">{coordinator.name}</p>
                                                                {coordinator.phone && (
                                                                    <p className="text-gray-600 text-xs sm:text-sm break-all">
                                                                        📞 +{coordinator.phone}
                                                                    </p>
                                                                )}
                                                                {coordinator.role && (
                                                                    <p className="text-amber-600 text-xs sm:text-sm font-medium break-words">
                                                                        {coordinator.role}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Training Video Section (if available) */}
                {/* {khidmatDetail.trainingVideo && (
                    <section className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center font-serif">
                            Training Resources
                        </h2>
                        <div className="text-center">
                            <a
                                href={khidmatDetail.trainingVideo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-8 py-4 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                                </svg>
                                Watch Training Video
                            </a>
                        </div>
                    </section>
                )} */}

                {/* Call to Action */}
                <section className="text-center bg-gradient-to-r from-amber-600 via-amber-700 to-amber-600 text-white p-6 sm:p-8 rounded-2xl shadow-lg mx-2 sm:mx-0">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 break-words">
                        Ready to Join {khidmatDetail.title}?
                    </h2>
                    <p className="text-base sm:text-lg mb-6 break-words px-2">
                        Register now via WhatsApp and be part of this noble service
                    </p>
                    <a
                        href={`${WHATSPP_LINK}?text=I%20want%20to%20register%20for%20${encodeURIComponent(khidmatDetail.title)}%20khidmat`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl"
                    >
                        <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.309" />
                        </svg>
                        Register via WhatsApp
                    </a>
                </section>
            </div>
        </div>
    );
};

export default KhidmatDetail;